import React, { useState, useEffect } from "react";
import { X, Ban } from "lucide-react";
import { useTranslation } from "react-i18next";
import EventList from "./EventList";
import EventForm from "./EventForm";
import PlayerSelector from "./PlayerSelector";
import PlayerAssignmentSection from "./PlayerAssignmentSection";

const EventModal = ({
  isDarkMode,
  isPlayer,
  selectedDate,
  eventPeriod,
  startHHMM,
  selectedList,
  selectedItem,
  eventType,
  opponentName,
  trainingLabel,
  trainingDescription,
  durationMin,
  saving,
  playerId,
  players,
  applyToPlayers,
  onClose,
  onItemClick,
  onItemDelete,
  onTypeSelect,
  setStartHHMM,
  setEventPeriod,
  setDurationMin,
  setOpponentName,
  setTrainingLabel,
  setTrainingDescription,
  setApplyToPlayers,
  onSave,
  onDelete,
  isItemFromOverride,
  renderItemTitle,
  renderItemMeta,
  hhmmToMinutes,
  onSavePlayersList,
  assignedPlayers = [],
  setAssignedPlayers,
  onCancelMatch,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (selectedItem?.player_ids) {
      if (setAssignedPlayers) setAssignedPlayers(selectedItem.player_ids);
    }
  }, [selectedItem?.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`p-6 rounded-lg shadow-lg w-[500px] max-h-[90vh] overflow-y-auto ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-black"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">{selectedDate}</h2>
            <p className="text-sm opacity-70">
              {startHHMM
                ? t("calendar_ui.time", { time: startHHMM })
                : t("calendar_ui.select_type")}
              {eventPeriod === "morning"
                ? t("calendar_ui.morning_suffix")
                : t("calendar_ui.afternoon_suffix")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-600/30 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Event List */}
        {selectedList.length > 0 && (
          <EventList
            isDarkMode={isDarkMode}
            isPlayer={isPlayer}
            selectedList={selectedList}
            selectedItem={selectedItem}
            onItemClick={onItemClick}
            onItemDelete={onItemDelete}
            isItemFromOverride={isItemFromOverride}
            renderItemTitle={renderItemTitle}
            renderItemMeta={renderItemMeta}
          />
        )}

        {/* Event Form */}
        <EventForm
          isDarkMode={isDarkMode}
          eventType={eventType}
          startHHMM={startHHMM}
          durationMin={durationMin}
          opponentName={opponentName}
          trainingLabel={trainingLabel}
          trainingDescription={trainingDescription}
          onTypeSelect={onTypeSelect}
          setStartHHMM={setStartHHMM}
          setEventPeriod={setEventPeriod}
          setDurationMin={setDurationMin}
          setOpponentName={setOpponentName}
          setTrainingLabel={setTrainingLabel}
          setTrainingDescription={setTrainingDescription}
          hhmmToMinutes={hhmmToMinutes}
        />

        {eventType === "training" && !isPlayer && players.length > 0 && (
          <PlayerAssignmentSection
            isDarkMode={isDarkMode}
            players={players}
            assignedPlayers={assignedPlayers}
            setAssignedPlayers={setAssignedPlayers}
          />
        )}

        {isPlayer && playerId && (
          <PlayerSelector
            isDarkMode={isDarkMode}
            players={players}
            playerId={playerId}
            applyToPlayers={applyToPlayers}
            setApplyToPlayers={setApplyToPlayers}
            selectedDate={selectedDate}
          />
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-2 pt-3 border-t border-gray-600/30">
          <button
            onClick={onSave}
            disabled={saving || !eventType}
            className={`px-4 py-2 rounded-md text-white font-medium transition ${
              saving || !eventType
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {saving ? t("common.saving") : selectedItem ? t("calendar_ui.update") : t("calendar_ui.create")}
          </button>

          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md transition ${
              isDarkMode
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {t("common.cancel")}
          </button>

          {!isPlayer && selectedItem && (
            <button
              onClick={onDelete}
              className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition"
            >
              {t("calendar_ui.delete")}
            </button>
          )}

          {!isPlayer && selectedItem && selectedItem.type === "match" && onCancelMatch && (
            <button
              onClick={() => onCancelMatch(selectedItem)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700 transition"
            >
              <Ban size={15} />
              {t("calendar_ui.cancel_match")}
            </button>
          )}
        </div>

        {isPlayer && (
          <p className="mt-3 text-xs opacity-70">{t("calendar_ui.override_hint")}</p>
        )}
      </div>
    </div>
  );
};

export default EventModal;
